import { z } from "zod";
import { Contact, ContactType } from "@/api/SolarCloudApi";
import { FormCard2 } from "@/components/FormCard2";
import { useRegisterContact } from "@/queries/useRegisterContact";
import { toast } from "sonner";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/Button";
import { CustomPhoneInput } from "@/components/ui/CustomPhoneInput";
import { useGeoCountry } from "@/queries/countries/useGeoCountry";

const registerContactSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  givenName: z.string().min(1, "Please enter your first name"),
  familyName: z.string().min(1, "Please enter your last name"),
  phoneNumber: z.string().min(1, "Please enter your phone number"),
});

type RegisterContactFormData = z.infer<typeof registerContactSchema>;

type RegisterContactDetailsFormProps = {
  className?: string;
};

export function RegisterContactDetailsForm({
  className,
}: RegisterContactDetailsFormProps) {
  const { data: geoCountry } = useGeoCountry();

  const registerContact = useRegisterContact({
    mutationConfig: {
      onSuccess: () => {
        toast.success(
          "Contact details registered successfully! We'll be in touch soon."
        );
      },
      onError: (error) => {
        toast.error("Failed to register contact details. Please try again.");
        console.error("Registration error:", error);
      },
    },
  });

  const onSubmit = (data: RegisterContactFormData) => {
    const contact: Contact = {
      ...data,
      type: ContactType.INQUIRY,
      country: geoCountry?.code ?? "unknown",
      comment: `Registering interest for country (${
        geoCountry?.name ?? "unknown"
      })`,
    };

    registerContact.mutate(contact);
  };

  return (
    <FormCard2
      schema={registerContactSchema}
      onSubmit={onSubmit}
      title="Register Interest"
      description="Leave your details and we'll be in touch when SolarCloud is available in your area."
      className={className}
    >
      {(form) => (
        <>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="your.email@example.com"
                    autoComplete="email"
                    {...field}
                    className="bg-brand-neutral-800"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="givenName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="John"
                    autoComplete="given-name"
                    {...field}
                    className="bg-brand-neutral-800"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="familyName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Doe"
                    autoComplete="family-name"
                    {...field}
                    className="bg-brand-neutral-800"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <CustomPhoneInput
                    value={field.value}
                    onChange={field.onChange}
                    allowAllCountries={true}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            disabled={registerContact.isPending}
            className="w-full"
          >
            {registerContact.isPending
              ? "Registering..."
              : "Register for Updates"}
          </Button>
        </>
      )}
    </FormCard2>
  );
}
